<template>
  <div class="requisition-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />
    <div
      v-if="!listLoading && !pageLoading"
    >
      <NoDataBlock
        v-if="!requisitions.length"
        :label="labels[$route.name]"
        class="ml-6 mr-6"
        document-type="Requisitions"
      >
        <template #content>
          <DpplPageLoader v-if="otherRequisitionTypesLoading" />
          <div
            v-else-if="sortedByLargestTextTotals.length"
            class="mt-8"
          >
            <div
              v-for="({ mode: sortedMode, total }) in sortedByLargestTextTotals"
              :key="sortedMode"
              class="result text-textDarkest"
            >
              Found
              <router-link :to="{ name: sortedMode, query: otherDocumentsQuery(sortedMode) }" class="text-textBrand">
                {{ total }}
                {{ types[sortedMode] || 'Requisitions' }}
              </router-link>
              in {{ labels[sortedMode] || '' }}
              {{ types[sortedMode] || 'Requisitions' }} Library
            </div>
          </div>
        </template>
      </NoDataBlock>

      <div
        v-else
        class="document-list-width"
      >
        <StickyListHeader
          class="pl-6 pr-6"
          :document-class="$route.name"
          :header-columns="headerColumns"
          :selected-sort="selectedSort"
          :sort-direction="sortDirection"
          @submit-sort="submitSort"
        />
        <LibraryList>
          <LibraryListItemWrapper
            v-for="{
              ulid: reqUuid,
              title: title,
              ats_requisition_label: atsReqId,
              department_name: departmentName,
              hiring_managers: hiringManagers,
              total_job_posts: totalJobPosts,
              draft_job_posts: draftJobPosts,
              published_job_posts: publishedJobPosts,
              unpublished_job_posts: unpublishedJobPosts,
              total_applicants: totalApplicants,
              active_applicants: activeApplicants,
              active_review_applicants: activeReviewApplicants,
              active_assessment_applicants: activeAssessmentApplicants,
              active_screen_applicants: activeScreenApplicants,
              active_offer_applicants: activeOfferApplicants,
              opened_at: openedAt,
              closed_at: closedAt,
              is_onhold: onHold,
            } in requisitions"
            :key="reqUuid"
          >
            <router-link
              :to="{ name: 'requisition-report', params: { id: reqUuid } }"
              class="document-list-item-link"
              @click="handleListItemClick(reqUuid)"
            >
              <DraftRequisitionListItem
                v-if="$route.name === 'draft-requisitions'"
                class="draft-requisitions pl-6 pr-6"
                :title="title"
                :on-hold="onHold"
                :ats-req-id="atsReqId"
                :department-name="departmentName"
                :hiring-managers="hiringManagers"
              />
              <OpenRequisitionListItem
                v-if="$route.name === 'open-requisitions'"
                class="open-requisitions pl-6 pr-6"
                :title="title"
                :on-hold="onHold"
                :ats-req-id="atsReqId"
                :draft-job-posts="draftJobPosts"
                :published-job-posts="publishedJobPosts"
                :unpublished-job-posts="unpublishedJobPosts"
                :total-job-posts="totalJobPosts"
                :active-applicants="activeApplicants"
                :active-review-applicants="activeReviewApplicants"
                :active-assessment-applicants="activeAssessmentApplicants"
                :active-screen-applicants="activeScreenApplicants"
                :active-offer-applicants="activeOfferApplicants"
                :total-applicants="totalApplicants"
                :opened-at="openedAt"
                :department-name="departmentName"
                :hiring-managers="hiringManagers"
              />
              <ClosedRequisitionListItem
                v-if="$route.name === 'closed-requisitions'"
                class="closed-requisitions pl-6 pr-6"
                :title="title"
                :on-hold="onHold"
                :ats-req-id="atsReqId"
                :draft-job-posts="draftJobPosts"
                :published-job-posts="publishedJobPosts"
                :unpublished-job-posts="unpublishedJobPosts"
                :total-job-posts="totalJobPosts"
                :total-applicants="totalApplicants"
                :closed-at="closedAt"
                :department-name="departmentName"
                :hiring-managers="hiringManagers"
              />
            </router-link>
          </LibraryListItemWrapper>
        </LibraryList>
      </div>
      <StickyPagination
        v-if="totalPages > 1"
        :model-value="page"
        :length="totalPages"
        @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router: $router })"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import DpplPageLoader from '@/components/datapeople/DpplPageLoader.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import LibrariesSkeletonLoader from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import DraftRequisitionListItem from '@/components/requisitions/requisitionListItems/DraftRequisitionListItem.vue';
import OpenRequisitionListItem from '@/components/requisitions/requisitionListItems/OpenRequisitionListItem.vue';
import ClosedRequisitionListItem from '@/components/requisitions/requisitionListItems/ClosedRequisitionListItem.vue';
import { useRequisitionLibrary } from '@/stores/requisitions/requisitionLibrary';
import { useAppPagination } from '@/stores/app/pagination';
import { useAppLoading } from '@/stores/app/loading';
import { useTracking } from '@/stores/tracking/tracking';
import { useFilters } from '@/stores/filters/filters';
import { useSubMenu } from '@/stores/filters/subMenu';
import { stickyHeadersRequisitions } from '@/helpers/constants/stickyHeaders';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { getDefaultReqSort } from '@/stores/requisitions/helpers';
import { getDefaultPostSort } from '@/stores/library/helpers';

export default {
  name: 'RequisitionList',
  components: {
    LibraryList,
    LibraryListItemWrapper,
    StickyPagination,
    NoDataBlock,
    LibrariesSkeletonLoader,
    DpplPageLoader,
    StickyListHeader,
    DraftRequisitionListItem,
    OpenRequisitionListItem,
    ClosedRequisitionListItem,
  },
  props: {
    mode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      animationDone: false,
      labels: {
        published: 'Published',
        unpublished: 'Unpublished',
        draft: 'Draft',
        flagship: 'Flagship',
        'closed-requisitions': 'Closed',
        'open-requisitions': 'Open',
        'draft-requisitions': 'Draft',
      },
      types: {
        email: 'Emails',
        published: 'Posts',
        unpublished: 'Posts',
        flagship: 'Posts',
        draft: 'Posts',
      },
    };
  },
  computed: {
    ...mapState(useRequisitionLibrary, ['requisitions', 'otherRequisitionTypesLoading', 'otherRequisitionTypeTotals', 'sortOptions']),
    ...mapState(useAppPagination, ['totalPages', 'page']),
    ...mapState(useAppLoading, ['pageLoading', 'listLoading', 'sidebarLoading']),
    ...mapState(useFilters, ['myJobsEnabled', 'searchTerm']),
    ...mapState(useSubMenu, ['selectedSubMenuOption']),
    selectedSort() {
      const date = this.$route.name === 'open-requisitions' ? '-opened_at' : '-closed_at';
      return this.selectedSubMenuOption?.replace('-', '') || date;
    },
    sortDirection() {
      return this.selectedSubMenuOption?.startsWith('-') ? 'desc' : 'asc';
    },
    libraryLoading() {
      return this.sidebarLoading || this.pageLoading || this.listLoading;
    },
    sortedByLargestTextTotals() {
      const order = ['draft-requisitions', 'open-requisitions', 'closed-requisitions', 'unpublished', 'published', 'flagship', 'draft', 'email'];

      return [...this.otherRequisitionTypeTotals].sort(({ mode: aMode }, { mode: bMode }) => {
        const aIndex = order.indexOf(aMode);
        const bIndex = order.indexOf(bMode);

        return aIndex - bIndex;
      }).filter(({ total }) => Boolean(total));
    },
    headerColumns() {
      const headerPerType = {
        'draft-requisitions': [
          'title',
          'reqId',
          'department',
          'hiringManager',
        ],
        'open-requisitions': [
          'title',
          'reqId',
          'livePosts',
          'activeApps',
          'daysOpen',
          'department',
          'hiringManager',
        ],
        'closed-requisitions': [
          'title',
          'reqId',
          'totalPosts',
          'totalApps',
          'closedAt',
          'department',
          'hiringManager',
        ],
      }[this.$route.name];

      return headerPerType
        .map((header) => {
          const headerObj = stickyHeadersRequisitions[header];
          const headerId = stickyHeadersRequisitions[header].id;
          headerObj.sortable = this.sortOptions.includes(headerId);
          return headerObj;
        })
        .filter(Boolean);
    },
  },
  async mounted() {
    subscribeToFilterUpdates(this.loadPage);

    const routeQuery = this.$route.query || {};

    this.setFilterConfig({
      module: 'requisitions',
      endpoint: 'requisitionLibrary',
      showMyJobs: true,
      datepicker: false,
    });

    await this.loadPage({
      mode: this.mode,
      routeQuery: routeQuery || {},
      router: this.$router,
      page: routeQuery.page || 1,
      searchTerm: routeQuery?.q || '',
      selectedMyJobs: routeQuery?.owned,
    });
  },
  beforeUnmount() {
    unsubscribeToFilterUpdates();

    this.setFilterConfig({
      subMenuType: null,
    });
  },
  methods: {
    ...mapActions(useRequisitionLibrary, ['loadRequisitions']),
    ...mapActions(useFilters, ['sendFilterRequest', 'submitSubMenu', 'loadFilters', 'setFilterConfig']),
    async loadPage(params) {
      return Promise.all([
        this.loadRequisitions(params),
        this.loadFilters({ ...params, sort: this.selectedSubMenuOption }),
      ]);
    },
    otherDocumentsQuery(mode) {
      let sort = getDefaultPostSort(mode);

      if (mode.includes('requisitions')) {
        sort = getDefaultReqSort(mode.replace('-requisitions', ''));
      }

      return {
        ...this.$route.query,
        sort,
      };
    },
    submitSort(selectedId) {
      if (selectedId === this.selectedSort) {
        const optionId = this.selectedSubMenuOption.includes('-') ? this.selectedSort : `-${this.selectedSort}`;

        this.submitSubMenu({ optionId, router: this.$router });
      } else {
        const isDateSort = selectedId === 'opened_at' || selectedId === 'closed_at';
        const dateSort = this.$route.name === 'open-requisitions' ? '-opened_at' : '-closed_at';
        const optionId = isDateSort ? dateSort : selectedId;

        this.submitSubMenu({ optionId, router: this.$router });
      }
    },
    handleListItemClick(reqUuid) {
      useTracking().requisitionOpenReqReport(reqUuid);
    },
    async updatePagination(page) {
      await this.loadPage({
        mode: this.mode,
        page,
        router: this.$router,
      });
    },
  },
};
</script>

<style lang="less" scoped>
.document-list-width {
  min-width: 1100px;
  padding:0 24px;
}

.document-list-item-link {
  color: rgb(var(--v-theme-textLight));

  &:visited {
    color: rgb(var(--v-theme-textLight));
  }
}

:deep(.sticky-header-items) {
  height: 100%;
}

.draft-requisitions, :deep(.sticky-header-items.draft-requisitions) {
  grid-template-columns: minmax(240px, 1fr) 92px minmax(134px, 184px) minmax(104px, 134px);
}

.open-requisitions, :deep(.sticky-header-items.open-requisitions) {
  grid-template-columns: minmax(240px, 1fr) repeat(4, 92px) minmax(134px, 184px) minmax(104px, 134px);
}

.closed-requisitions, :deep(.sticky-header-items.closed-requisitions) {
  grid-template-columns: minmax(240px, 1fr) repeat(4, 92px) minmax(134px, 184px) minmax(104px, 134px);
}

:deep(.v-skeleton-loader__list-item-two-line) {
  transform: translateY(-10px);
}

.result {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
}
</style>

<style lang="less">
.datapeople-app .datapeople-tooltip.requition-list-tooltip.v-tooltip__content {
  padding: 8px 16px !important;
  width: auto;
}
</style>
